/**
 * @fileoverview The Gibbs action dispatcher -- the registered seam between main and the Gibbs
 * tree (proto/src/gibbs/). See docs/gibbs-migration-register.md, Slice D item 1.
 *
 * interpretActionPlan takes an ActionPlan from the text-to-action pipeline and executes a
 * single_action plan through the registry: look the ActionDef up, gate on status, validate the
 * plan, bind the declared inputs and parameters, and dispatch -- to a UserAction, a BFD server
 * command, or a job submission. Workflow, chat and clarify plans pass through for the UI.
 *
 * This module is the only file outside gibbs/ allowed to import from it
 * (test/bmaps/gibbsImportBoundary.test.js). The two imports are dynamic to avoid a load-time
 * cycle: cmds -> gibbs -> cmds. CmdInterpreter keeps a static forwarder to interpretActionPlan
 * so callers, and the fork's tests that patch it, keep the CmdInterpreter.interpretActionPlan
 * name.
 *
 * The dispatcher arrived from the fork's CmdInterpreter.js (payload 5736c4bfc) and was moved
 * here unchanged apart from the argStyle branch and this seam; see the register for what was
 * corrected and what was recorded rather than fixed (the server-command route).
 */
import { UserActions } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { energyErrorText, parseEnergyError } from './cmds_common';

/**
 * How the special values "active", "current" and "all" resolve to workspace objects, by the
 * DECLARED type of the input or parameter they were supplied for. A value supplied for anything
 * else -- a string option like export-to-format's `target: 'all'` or the hydrogen view's
 * `mode: 'all'` -- is a literal and passes through untouched. (The fork guessed from the
 * parameter's name instead, which turned `target: 'all'` into an array of case data.)
 */
const SPECIAL_VALUE_RESOLVERS = {
    // A single Compound cannot be "all"; that literal stays a literal (activate-compound).
    Compound: {
        active: (ws) => ws.getActiveCompound?.() ?? null,
    },
    CompoundList: {
        active: (ws) => ws.getActiveCompound?.() ?? null,
        all: (ws) => ws.getLoadedCompounds?.() || [],
    },
    CaseData: {
        current: (ws) => ws.firstCaseData?.() || null,
        all: (ws) => ws.allCaseData?.() || [],
    },
    'CaseData[]': {
        current: (ws) => ws.firstCaseData?.() || null,
        all: (ws) => ws.allCaseData?.() || [],
    },
    // No entry for ProteinStructure: the registry declares it only on outputs.
};

/**
 * The types the table above resolves, each with its special values. The registry's
 * PLAN_SPECIAL_VALUES must match; test/gibbs/dispatchTargets.test.js checks it. Exported for
 * that test.
 */
export const SPECIAL_VALUES = Object.freeze(Object.fromEntries(
    Object.entries(SPECIAL_VALUE_RESOLVERS).map(([type, byValue]) => [type, Object.keys(byValue)])
));

/**
 * Resolve one supplied value against the ParamDef it was supplied for. Exported for its unit
 * test; the dispatcher passes App.Workspace.
 *
 * @param {*} value - the value the plan supplied
 * @param {{ name: string, type: string } | undefined} def - the declared input or parameter
 * @param {object} workspace - App.Workspace or a stand-in
 */
export function resolveSpecialValue(value, def, workspace) {
    if (typeof value !== 'string' || !def) return value;
    const resolver = SPECIAL_VALUE_RESOLVERS[def.type]?.[value];
    return resolver ? resolver(workspace) : value;
}

/** The declared inputs and parameters of an ActionDef, by name. */
function declaredParams(actionDef) {
    const byName = new Map();
    for (const def of [...actionDef.inputs, ...actionDef.parameters]) byName.set(def.name, def);
    return byName;
}

/**
 * The declared inputs that were supplied, keyed by name: the shape an `argStyle: 'options'`
 * action hands to its userAction (see gibbs/registry/schema.js). Undefined values are left out
 * so an optional input that was not given does not appear as an explicit `undefined` key.
 */
function namedInputs(actionDef, resolvedParams) {
    const named = {};
    for (const input of actionDef.inputs) {
        const value = resolvedParams[input.name];
        if (value !== undefined) named[input.name] = value;
    }
    return named;
}

function returnedErrorMessage(error) {
    // The server's JSON-encoded errors, worded as the Selector words them.
    const parsed = parseEnergyError(error);
    if (parsed?.errType) return energyErrorText(parsed);
    if (typeof error === 'string') return error;
    if (error && typeof error.message === 'string') return error.message;
    if (error && typeof error.code === 'string') return error.code;
    return 'Action reported an error.';
}

/**
 * Whether an error value is a bare machine code rather than something to show a chemist:
 * a single token, no whitespace -- 'service_unavailable', 'NO_MATCH'.
 */
function isBareErrorCode(error) {
    return typeof error === 'string' && error.trim().length > 0 && !/\s/.test(error.trim());
}

/**
 * What to report for a failed result: the callee's own sentence when it wrote one, otherwise
 * the error value.
 *
 * Measured on jfreeze 2026-10-16: "swap a phenyl ring for a pyridine" answered the single word
 * **`service_unavailable`**. The callee had already composed "Substructure replacement service
 * is unavailable. The endpoint may not be deployed yet." into `summary` and returned it
 * alongside `error`, and this function threw the sentence away in favour of the code -- the
 * same shape as the shortfall result `recordActionResult` used to discard.
 *
 * Only a BARE CODE defers to the summary. An error that is already a sentence stays, because it
 * is usually more specific than a summary written before the failure was known.
 */
function failureMessage(result) {
    const { error, summary } = result;
    if (isBareErrorCode(error) && typeof summary === 'string' && summary.trim().length > 0) {
        return summary.trim();
    }
    return returnedErrorMessage(error);
}

/**
 * What to say after a plan ran, in order of how much it is worth saying.
 *
 * **Why this is not just the plan's `summary`.** A plan summary is written by the planner
 * BEFORE anything runs, so narrating it as an outcome is the confident-wrongness failure in
 * miniature -- it describes intent in the past tense. So the callees' own summaries come first:
 * those are return values, and a return value is evidence. The plan summary is used only as a
 * label for what was attempted, and only when every planned step actually produced a result.
 *
 * **Why the plan summary is used at all.** Measured on jfreeze 2026-10-16: asking to screen
 * against the kinase panel answered the bare words `Done: open-screening-panel`, throwing away
 * the summary the shortcut had already composed -- "Opening the Screening panel. Choose the
 * human kinase panel as the target library, pick the compounds to screen, and submit from
 * there." The chemist had just answered a question about which library and the reply did not
 * mention it, which reads as if the answer was discarded. (It partly was: the route cannot
 * preselect the library. Saying so is the honest version.)
 *
 * @param {Object} plan the plan as the planner built it
 * @param {{results: object[]}} results the execution record
 * @returns {string} chat text; never empty
 */
export function planNarration(plan, results, describe, payloadText) {
    const ran = results.results || [];

    // The result's own words always win over a plan summary. `describeExecution` reports a no-op
    // as a no-op, reads an array payload as the answer it is, and renders a viewer receipt in the
    // viewer's own words -- all from the return value, which a plan summary is not.
    const anyNoOp = ran.some((e) => e.noOp);
    const spoke = ran.length === 1 && Boolean(payloadText(ran[0].result));
    // More than one result goes here too: interleaving a plan summary with several outcomes
    // produces a message no one reads, and describeExecution already names them all.
    if (anyNoOp || spoke || ran.length > 1) return describe(ran);

    // Nothing in the result spoke. The plan's own summary is the next best thing, but it is
    // INTENT written before execution, so it is only honest when every planned step produced a
    // result -- a plan that ran 1 of 3 steps without erroring is not described by a summary of
    // all 3.
    const planned = (plan.actions || []).length;
    const summary = typeof plan.summary === 'string' ? plan.summary.trim() : '';
    if (summary && planned > 0 && ran.length === planned) return summary;

    return describe(ran);
}

/**
 * Record one dispatch result without letting a returned failure pass as success. Main's commands
 * report failure three ways: a throw (the dispatcher catches it), a singular `error`, or a
 * non-empty `errors` array, which most server-backed commands return. The last two are reported
 * here, and a result carrying `errors` is still kept.
 */
function recordActionResult(results, entry) {
    const { result } = entry;
    if (result?.error) {
        results.errors.push(failureMessage(result));
        return;
    }
    if (Array.isArray(result?.errors) && result.errors.length > 0) {
        results.errors.push(...result.errors.map(returnedErrorMessage));
    }
    // The ONE no-op signal this tree carries. A command's own `isNoOp` predicate has no
    // expression here (docs/gibbs-migration-register.md, "What isNoOp actually does"), so a
    // receipt saying `changed: false` is the whole of the available evidence -- and evidence is
    // the bar: a null or undefined return is NOT a no-op, because many commands do real work
    // and return nothing. describeExecution's header holds the same line.
    results.results.push(
        result && typeof result === 'object' && result.changed === false
            ? { ...entry, noOp: true }
            : entry,
    );
}

/**
 * Dispatch a BFD server command or search service request.
 * Handles actions that talk directly to the server without
 * a UserAction wrapper (translate-molecule, search endpoints,
 * property lookups, etc.).
 *
 * @param {string} command - Server command name
 * @param {Object} params - Resolved parameters
 * @param {Object} actionDef - Full ActionDef for context
 * @returns {Promise<Object>} Server response
 */
export async function dispatchServerCommand(command, params, actionDef) {
    const { ServerCmds } = await import('../server_cmds');

    // Map serverCommand names to ServerCmd objects
    const cmdMap = {
        'translate-molecule': ServerCmds.TranslateMolecule,
        'get-molecule-properties': ServerCmds.GetMoleculeProperties,
        'get-unbound-conformations': ServerCmds.GetUnboundConformations,
        'export-selection': ServerCmds.ExportSelection,
        select: ServerCmds.Select,
        'frag-data-query': ServerCmds.FragDataQuery,
    };

    const serverCmd = cmdMap[command];
    if (serverCmd) {
        // ⚠️ THIS CALL CANNOT SUCCEED, and the map above is aspirational. Recorded 2026-10-16
        // rather than fixed, because the fix is the server-command route itself, which the
        // migration register lists as not taken on this line.
        //
        // Two things are wrong, and the second survives fixing the first:
        //   1. `ServerCmd` has no `execute` method. Its methods are `cmdData(...args)`, the
        //      GetRequestId / withRequestId / addRequestIdToBinaryCmd statics, and `label()`.
        //      So this throws "serverCmd.execute is not a function".
        //   2. `cmdData(...args)` SPREADS positionally into the command-text builder, and those
        //      builders take positional arguments -- `cmdTextTranslateMolecule(molID, inType,
        //      moldata, outType)` -- while this hands over one `params` object. Unlike the
        //      UserAction route, argument ORDER is load-bearing here.
        //
        // This is why every `serverCommand` action carries `blockedOn: 'server-route'`, and why
        // that reason is load-bearing: remove it and a plan reaches this line and gets a
        // TypeError instead of a clean refusal. The 14 of them are pinned in
        // test/gibbs/dispatchTargets.test.js.
        //
        // Whoever implements this wants `declaredParams`/`namedInputs` above, which already do
        // the positional spread for the UserAction route, plus a response adapter keyed on the
        // ServerCmd's own `handlerNames` (its ResponseIds).
        return serverCmd.execute(params);
    }

    // Search/edit service endpoints (HTTP, not WebSocket)
    const { FragservWs, WebServices } = await import('../WebServices');
    if (command === 'lookupByName') {
        return FragservWs.fragLookupByName(params.names);
    }
    const serviceMap = {
        substructureSearch: {
            url: `${WebServices.FRAGSERV_HOST}/edit/substructureSearch`,
            method: 'POST',
        },
        searchEngine: {
            url: `${WebServices.FRAGSERV_HOST}/search/searchEngine`,
            method: 'POST',
        },
        getMolPic: {
            url: `${WebServices.FRAGSERV_HOST}/edit/pubpic/`,
            method: 'GET',
        },
        getProperties: {
            url: `${WebServices.FRAGSERV_HOST}/search/properties/`,
            method: 'POST',
        },
    };

    const svc = serviceMap[command];
    if (svc) {
        const response = await WebServices.startWsRequest(
            svc.url,
            svc.method === 'POST' ? JSON.stringify(params) : null
        );
        return typeof response === 'string'
            ? JSON.parse(response)
            : response;
    }

    throw new Error(
        `Unknown serverCommand: ${command}`
        + ` for action ${actionDef.id}`
    );
}

/**
 * Submit an async background job via the JobManager.
 * Handles folding, docking, scoring, REINVENT, MapLight,
 * and library screening jobs.
 *
 * @param {string} jobType - Job type from JobTypes enum
 * @param {Object} params - Resolved parameters
 * @param {Object} actionDef - Full ActionDef for context
 * @returns {Promise<Object>} Job submission result with jobId
 */
export async function dispatchJobSubmission(jobType, params, actionDef) {
    const { postJob } = await import('../jobman/JobsApi');

    const jobrequest = { ...params };
    const stash = {
        actionId: actionDef.id,
        actionName: actionDef.name,
    };

    const { results, error } = await postJob({
        jobtype: jobType,
        jobrequest,
        stash,
    });

    if (error) {
        throw new Error(
            `Job submission failed for ${actionDef.id}: ${error}`
        );
    }

    return {
        jobId: results.jobid,
        jobType,
        state: results.state || 'starting',
        message: `${actionDef.name} job submitted`,
    };
}

/**
 * Dispatch an ActionDef-based action plan from the Gibbs
 * text-to-action pipeline.
 *
 * For single_action plans: resolves parameters and executes
 * via UserActions, server commands, or job submission.
 * For workflow plans: returns the plan as pending
 * (for Workflow Checker UI).
 * For clarification/chat: passes through for UI rendering.
 *
 * @param {Object} plan - ActionPlan from textToAction()
 * @returns {Promise<Object>} Execution result
 */
export async function interpretActionPlan(plan, opts = {}) {
    // The workflow engine resolves a step's inputs from upstream OUTPUTS and from the live
    // workspace, so by the time it dispatches, `resolvedInputs` holds real objects -- a
    // CompoundList, a CaseData -- not the names a model-written plan is restricted to. The
    // checks below exist to stop an LLM naming such a thing; applied to the engine's own
    // resolved values they refused every chained step with
    //     "compounds" must be "active" or "all": a plan cannot name a CompoundList
    // which is why a workflow docked step 1 and then failed at step 2 forever.
    //
    // `params` are still plan literals and are still checked. Only the inputs the engine
    // resolved are exempt, and only when the engine says so.
    const inputsAlreadyResolved = opts.inputsAlreadyResolved === true;
    if (!plan || !plan.intent) {
        return { type: 'error', error: 'Invalid plan: missing intent' };
    }

    // Chat and clarification pass through for UI
    if (plan.intent === 'chat') {
        return { type: 'chat', message: plan.message || '' };
    }
    if (plan.intent === 'clarify') {
        return { type: 'clarification', clarification: plan };
    }

    // Workflow plans returned as pending — Workflow Checker UI handles confirmation
    if (plan.intent === 'workflow') {
        return {
            type: 'pending_workflow',
            plan,
            summary: plan.summary || 'Multi-step workflow ready for review',
        };
    }

    // Single action: execute immediately
    if (plan.intent === 'single_action' && plan.actions?.length > 0) {
        const planned = plan.actions[0];
        const results = { type: 'executed', results: [], errors: [] };

        try {
            // Dynamic import to avoid circular dependency at module load time.
            // This module is the registered seam of the Gibbs import boundary
            // (test/bmaps/gibbsImportBoundary.test.js); this import and the one below are it.
            // See docs/gibbs-migration-register.md, Slice D item 1.
            const {
                getAction, uncarriableParams, planValueProblem,
            } = await import('../gibbs/registry');
            const actionDef = getAction(planned.actionId);

            if (!actionDef) {
                results.errors.push(`Unknown action: ${planned.actionId}`);
                return results;
            }

            if (actionDef.status !== 'implemented') {
                results.errors.push(
                    `Action "${planned.actionId}" is not implemented`
                    + ` (status: ${actionDef.status || 'unspecified'}).`
                );
                return results;
            }

            // Built, but its call does not fit this tree yet (schema.js `blockedOn`). Refused
            // here rather than misbehaving in the callee; the blocked set is pinned by
            // test/gibbs/dispatchTargets.test.js.
            if (actionDef.blockedOn) {
                results.errors.push(
                    `Action "${planned.actionId}" is not implemented on this build`
                    + ` (blocked on: ${actionDef.blockedOn}).`
                );
                return results;
            }

            // A value a plan cannot carry would reach the callee as a name or as unchecked data
            // where it needs a live object (registry types.js). A required one means the action
            // cannot run from a plan; a supplied one must be a value the plan can carry -- for
            // a resolved type, only its special values.
            const uncarried = inputsAlreadyResolved ? [] : uncarriableParams(actionDef);
            for (const def of uncarried) {
                results.errors.push(
                    `Action "${planned.actionId}" cannot take "${def.name}" from a plan`
                    + ` (type ${def.type}, which a plan cannot carry).`
                );
            }
            const supplied = inputsAlreadyResolved
                ? { ...(planned.params || {}) }
                : { ...(planned.params || {}), ...(planned.resolvedInputs || {}) };
            const defs = declaredParams(actionDef);
            for (const [key, value] of Object.entries(supplied)) {
                if (uncarried.some((def) => def.name === key)) continue;
                const problem = planValueProblem(defs.get(key), value);
                if (problem) results.errors.push(`Action "${planned.actionId}": ${problem}.`);
            }
            if (results.errors.length > 0) return results;

            // A destructive action runs only on a plan that carries an explicit confirmation.
            // The planner stages such a plan and sets `confirmed: true` only when the user
            // confirms it (gibbs/text-to-action/resumeFromClarification.js); nothing else may
            // set it, so nothing destructive dispatches unasked.
            if (actionDef.destructiveConfirm === true && plan.confirmed !== true) {
                results.errors.push(
                    `Action "${planned.actionId}" is destructive and needs confirmation:`
                    + ` ${actionDef.confirmMessage}`
                );
                return results;
            }

            const { validatePlan } = await import('../gibbs/text-to-action/validatePlan');
            const validation = validatePlan(plan);
            if (!validation.valid) {
                results.errors.push(...validation.errors);
                return results;
            }

            // Resolve special values ("active", "current", "all") by the declared type of the
            // input or parameter each was supplied for.
            const resolvedParams = {};
            for (const [key, value] of Object.entries(supplied)) {
                resolvedParams[key] = resolveSpecialValue(value, defs.get(key), App.Workspace);
            }

            // Required inputs are nullish-invalid. Preserve optional positional holes so a
            // missing earlier input can never re-index a later input at dispatch.
            const missingRequired = actionDef.inputs
                .filter((input) => input.required !== false
                    && resolvedParams[input.name] == null)
                .map((input) => input.name);
            if (missingRequired.length > 0) {
                results.errors.push(
                    `Missing required input(s) for ${planned.actionId}: ${missingRequired.join(', ')}`
                );
                return results;
            }
            const inputArgs = actionDef.inputs
                .map((input) => resolvedParams[input.name]);
            const paramObj = {};
            for (const p of actionDef.parameters) {
                if (resolvedParams[p.name] !== undefined) {
                    paramObj[p.name] = resolvedParams[p.name];
                }
            }

            // Dispatch priority: userAction → serverCommand → jobType
            if (actionDef.userAction
                && UserActions[actionDef.userAction]) {
                // Route 1: UserAction function (most actions). The declaration is the
                // signature: inputs spread positionally in declared order, parameters as one
                // trailing options object. An ActionDef with `argStyle: 'options'` instead
                // hands its callee ONE named object holding inputs and parameters alike --
                // the shape main's named-object commands take (alignProtein(options)).
                const args = actionDef.argStyle === 'options'
                    ? [{ ...namedInputs(actionDef, resolvedParams), ...paramObj }]
                    : [...inputArgs];
                if (actionDef.argStyle !== 'options' && Object.keys(paramObj).length > 0) {
                    args.push(paramObj);
                }
                const result = await UserActions[
                    actionDef.userAction
                ](...args);
                recordActionResult(results, {
                    actionId: planned.actionId,
                    actionName: actionDef.name,
                    result,
                    displayText: actionDef.name,
                });
            } else if (actionDef.serverCommand) {
                // Route 2: BFD server command (translate,
                // properties, search service endpoints)
                const result = await dispatchServerCommand(
                    actionDef.serverCommand,
                    resolvedParams,
                    actionDef
                );
                recordActionResult(results, {
                    actionId: planned.actionId,
                    actionName: actionDef.name,
                    result,
                    displayText: actionDef.name,
                });
            } else if (actionDef.jobType) {
                // Route 3: Async job submission (folding,
                // docking, screening, REINVENT, MapLight)
                const result = await dispatchJobSubmission(
                    actionDef.jobType,
                    resolvedParams,
                    actionDef
                );
                recordActionResult(results, {
                    actionId: planned.actionId,
                    actionName: actionDef.name,
                    result,
                    displayText: `${actionDef.name} (job submitted)`,
                });
            } else {
                results.errors.push(
                    `Action "${planned.actionId}" has no`
                    + ' executable dispatch route (userAction:'
                    + ` ${actionDef.userAction}, serverCommand:`
                    + ` ${actionDef.serverCommand}, jobType:`
                    + ` ${actionDef.jobType})`
                );
            }
            // (macro recording is fed from UserAction.do() — the universal
            // seam that both manual and Gibbs-dispatched actions flow
            // through — so no publish is needed here.)
        } catch (err) {
            results.errors.push(`Error executing ${planned.actionId}: ${err.message}`);
        }

        // Built here rather than in the chat view so the wording is unit-testable and so the UI
        // file stays free of any judgement about what a result means. Dynamic, like the registry
        // import above: this module is the seam, and a static import of the Gibbs tree from
        // cmds/ is what the boundary test forbids.
        try {
            const { describeExecution, resultPayloadText } = await import('../gibbs/ui/describeExecution');
            results.narration = planNarration(plan, results, describeExecution, resultPayloadText);
        } catch (err) {
            // Narration is not the action. Losing the wording must not lose the dispatch.
            results.narration = null;
        }
        return results;
    }

    return { type: 'error', error: `Unhandled plan intent: ${plan.intent}` };
}

/**
 * Plan a natural language request and run what it produces -- the chat's entry point into the
 * Gibbs tree, so a caller names neither the planner nor the workspace.
 *
 * Returns the planner's own result, with `execution` added when a plan ran:
 *  - 'chat': a message to show. `chatSource` says whose it is -- 'planner' to show as it stands,
 *    'llm' for the caller's own conversational route to answer instead.
 *  - 'clarification': a question for the user. Pass `clarification.context` and their answer to
 *    resumeInterpretText. Destructive confirmations arrive this way, as a 'destructive_confirm'
 *    cascade; the plan stays with the planner until an answer comes back.
 *  - 'plan': a single action, already dispatched. `execution` holds the result.
 *  - 'invalid' or 'cancelled': nothing ran.
 *
 * @param {string} text - What the user asked for
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<Object>}
 */
export async function interpretText(text, { signal }={}) {
    const { textToAction } = await import('../gibbs/text-to-action');
    const result = await textToAction(text, App.Workspace, { signal });
    if (result.type !== 'plan') return result;
    return { ...result, execution: await interpretActionPlan(result.plan) };
}

/**
 * Answer an outstanding clarification and run the plan it completes, if it completes one. The
 * result shapes are interpretText's: another 'clarification' when more is needed, a dispatched
 * 'plan', or 'chat' carrying what the planner had to say instead -- a cancelled confirmation, or
 * one no longer pending.
 *
 * @param {Object} context - `clarification.context` from an earlier interpretText
 * @param {*} answer - An option's value, what the user typed, or {decision} for a confirmation
 * @returns {Promise<Object>}
 */
export async function resumeInterpretText(context, answer) {
    const { resumeFromClarification } = await import(
        '../gibbs/text-to-action/resumeFromClarification'
    );
    const {
        plan, needsMoreClarification, chatMessage, rewriteMessage,
    } = resumeFromClarification(context, answer, App.Workspace);
    if (needsMoreClarification) {
        return { type: 'clarification', clarification: needsMoreClarification };
    }
    // The screening-type and chemistry-disambiguation cascades do not answer with a plan. The
    // pick rewrites the ambiguous phrase inside the user's own message and asks for the whole
    // chain to be run again on it -- so dropping `rewriteMessage`, as this function used to,
    // turned those picks into an empty chat turn. Re-entering the chain is safe: the rewrite
    // replaces the phrase that was ambiguous, so it does not land back on the same cascade.
    if (rewriteMessage) {
        return interpretText(rewriteMessage);
    }
    if (plan) {
        // A resumed cascade can produce a MULTI-STEP plan -- a target pick becomes an acquire
        // workflow, a disease pick becomes the whole template. Those go to the Workflow Checker,
        // exactly as a multi-step plan from a first request does. Returning `type: 'plan'` for
        // them sent the chat turn down the branch that prints "Done.", while the plan itself
        // never reached the pane: nothing ran, and nothing was shown.
        if (plan.intent === 'workflow') {
            return {
                type: 'workflow',
                plan,
                request: context.originalMessage || '',
                chatSource: 'planner',
            };
        }
        return { type: 'plan', plan, execution: await interpretActionPlan(plan) };
    }
    return { type: 'chat', message: chatMessage || '', chatSource: 'planner' };
}

/**
 * The EventBroker topic the Workflow Checker listens on for a proposed plan.
 *
 * A multi-step plan is not run by the dispatcher: `interpretText` returns `{type: 'workflow'}`
 * and the pane owns review, parameter edits, the autonomy level and the start button. The chat
 * turn only has to publish it, which needs this one topic name -- and the name lives inside the
 * boundary, in `gibbs/ui/WorkflowPane`.
 *
 * It is exposed from here, rather than imported where it is published, so the Gibbs path stays
 * named only inside a registered seam (test/bmaps/gibbsImportBoundary.test.js). Publishing and
 * opening the pane remain in the UI layer, which is where EventBroker and UserActions belong.
 *
 * @returns {Promise<string>} The `WorkflowEvents.PROPOSED` topic.
 */
export async function workflowProposedEvent() {
    const { WorkflowEvents } = await import('../gibbs/ui/WorkflowPane/constants');
    return WorkflowEvents.PROPOSED;
}
